import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatDate, formatErrorDetail } from "@/lib/api";
import { PageHeader, Surface, TableWrap, Empty, Stat } from "@/components/app/Primitives";
import StatusBadge from "@/components/app/StatusBadge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import AnnouncementBoard from "@/components/app/AnnouncementBoard";
import { Users, FileText, Clock, Check, X } from "lucide-react";
import { toast } from "sonner";

// At-a-glance board: green = clocked in, red = not clocked in, orange = on leave.
// Click a person to drill into their day.
function AttendanceMonitor() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [detail, setDetail] = useState(null);
    const load = () => api.get("/team/attendance-today").then((r) => setData(r.data)).catch(() => {});
    useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);
    if (!data || !data.items?.length) return null;
    const s = data.summary;
    const tone = { present: "bg-emerald-50 border-emerald-300 text-emerald-800", absent: "bg-red-50 border-red-300 text-red-700", leave: "bg-orange-50 border-orange-300 text-orange-800" };
    const dot = { present: "bg-emerald-500", absent: "bg-red-500", leave: "bg-orange-400" };
    return (
        <Surface className="p-4 mb-4" data-testid="attendance-monitor">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <div className="flex items-center gap-2"><Clock className="size-4 text-primary" /><h3 className="font-display font-semibold">Attendance today · {data.date}</h3></div>
                <div className="flex items-center gap-3 text-xs">
                    <span className="inline-flex items-center gap-1"><span className="size-2 rounded-full bg-emerald-500" /> In {s.present}</span>
                    <span className="inline-flex items-center gap-1"><span className="size-2 rounded-full bg-red-500" /> Not in {s.absent}</span>
                    <span className="inline-flex items-center gap-1"><span className="size-2 rounded-full bg-orange-400" /> On leave {s.leave}</span>
                    {s.late > 0 && <span className="badge badge-pending">⏰ {s.late} late</span>}
                </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
                {data.items.map((m) => (
                    <div key={m.employee_id} className={`relative rounded-lg border p-2.5 cursor-pointer hover:shadow-sm ${tone[m.status]}`} onClick={() => setDetail(m)} data-testid={`att-${m.employee_no}`}>
                        {m.late_minutes > 0 && <span className="absolute -top-2 right-2 text-[9px] font-bold uppercase tracking-wide bg-amber-500 text-white rounded-full px-2 py-0.5 shadow">Late {m.late_minutes}m</span>}
                        <div className="flex items-center gap-2">
                            <span className={`size-2.5 rounded-full shrink-0 ${dot[m.status]}`} />
                            <div className="min-w-0">
                                <div className="text-sm font-semibold truncate">{m.name}</div>
                                <div className="text-[10px] opacity-80 truncate">
                                    {m.status === "leave" ? `On ${m.leave_type || "leave"}`
                                        : m.status === "present" ? `In ${m.time_in ? new Date(m.time_in).toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" }) : ""}`
                                        : "Not clocked in"}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
                <DialogContent className="max-w-sm">
                    <DialogHeader><DialogTitle>{detail?.name}</DialogTitle></DialogHeader>
                    {detail && (
                        <div className="space-y-1.5 text-sm">
                            <div className="flex justify-between"><span className="text-muted-foreground text-xs">Employee No.</span><span className="font-mono">{detail.employee_no}</span></div>
                            <div className="flex justify-between"><span className="text-muted-foreground text-xs">Status today</span>
                                <span className={`badge ${detail.status === "present" ? "badge-approved" : detail.status === "leave" ? "" : "badge-destructive"}`}>
                                    {detail.status === "present" ? "Clocked in" : detail.status === "leave" ? `On ${detail.leave_type || "leave"}` : "Not clocked in"}
                                </span>
                            </div>
                            {detail.time_in && <div className="flex justify-between"><span className="text-muted-foreground text-xs">Time in</span><span className="font-mono text-xs">{new Date(detail.time_in).toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" })}</span></div>}
                            {detail.shift_start && <div className="flex justify-between"><span className="text-muted-foreground text-xs">Shift start</span><span className="font-mono text-xs">{detail.shift_start}</span></div>}
                            {detail.late_minutes > 0 && <div className="flex justify-between"><span className="text-muted-foreground text-xs">Late by</span><span className="badge badge-pending">{detail.late_minutes} min</span></div>}
                            <button className="w-full h-9 mt-2 rounded-md border border-border text-sm"
                                onClick={() => navigate(`/timekeeping/attendance?start=${data.date}`)} data-testid="open-attendance-review">
                                Open full Attendance Review →
                            </button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </Surface>
    );
}

export default function TeamDashboard() {
    const navigate = useNavigate();
    const [employees, setEmployees] = useState([]);
    const [leaves, setLeaves] = useState([]);
    const [ot, setOt] = useState([]);
    const [att, setAtt] = useState([]);

    const load = async () => {
        const [e, l, o, a] = await Promise.all([
            api.get("/team/employees"),
            api.get("/team/leaves"),
            api.get("/team/overtime"),
            api.get("/team/attendance"),
        ]);
        setEmployees(e.data.items || []);
        setLeaves(l.data.items || []);
        setOt(o.data.items || []);
        setAtt(a.data.items || []);
    };
    useEffect(() => { load(); }, []);

    const empMap = Object.fromEntries(employees.map((e) => [e.id, `${e.last_name}, ${e.first_name}`]));

    const decide = async (kind, id, decision) => {
        try {
            await api.post(`/timekeeping/${kind}/${id}/approve`, { decision });
            toast.success(`${kind} ${decision}`); load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    const pendingLeaves = leaves.filter((l) => l.status === "submitted");
    const pendingOt = ot.filter((o) => o.status === "submitted");

    return (
        <div>
            <PageHeader title="My Team" subtitle="Direct reports, attendance, and pending approvals." />
            <AnnouncementBoard />
            <AttendanceMonitor />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                <Stat label="Team Size" value={employees.length} icon={Users} tone="primary" />
                <Stat label="Pending Leaves" value={pendingLeaves.length} icon={FileText} />
                <Stat label="Pending OT" value={pendingOt.length} icon={Clock} />
                <Stat label="Recent Attendance" value={att.length} icon={Clock} />
            </div>
            <Tabs defaultValue="team">
                <TabsList>
                    <TabsTrigger value="team" data-testid="tab-team-roster">Roster</TabsTrigger>
                    <TabsTrigger value="leaves" data-testid="tab-team-leaves">Leave Approvals ({pendingLeaves.length})</TabsTrigger>
                    <TabsTrigger value="overtime" data-testid="tab-team-ot">OT Approvals ({pendingOt.length})</TabsTrigger>
                    <TabsTrigger value="attendance" data-testid="tab-team-att">Attendance</TabsTrigger>
                </TabsList>
                <TabsContent value="team">
                    <TableWrap>
                        <thead><tr><th>Emp No</th><th>Name</th><th>Position</th><th>Status</th></tr></thead>
                        <tbody>
                            {employees.map((e) => (
                                <tr key={e.id} className="cursor-pointer" onClick={() => navigate(`/hris/employees/${e.id}`)} data-testid={`team-emp-${e.employee_no}`}>
                                    <td className="font-mono">{e.employee_no}</td>
                                    <td className="font-medium">{e.last_name}, {e.first_name}</td>
                                    <td>{e.position || "—"}</td>
                                    <td><StatusBadge value={e.employment_status} /></td>
                                </tr>
                            ))}
                            {!employees.length && <tr><td colSpan={4}><Empty title="No direct reports" subtitle="Ask HR to assign team members to your manager record." /></td></tr>}
                        </tbody>
                    </TableWrap>
                </TabsContent>
                <TabsContent value="leaves">
                    <TableWrap>
                        <thead><tr><th>Employee</th><th>Type</th><th>From</th><th>To</th><th>Days</th><th>Status</th><th>Action</th></tr></thead>
                        <tbody>
                            {leaves.map((l) => (
                                <tr key={l.id} data-testid={`team-leave-${l.id}`}>
                                    <td>{empMap[l.employee_id] || l.employee_id?.slice(0, 8)}</td>
                                    <td>{l.leave_type}</td>
                                    <td className="font-mono">{l.start_date}</td>
                                    <td className="font-mono">{l.end_date}</td>
                                    <td>{l.days}</td>
                                    <td><StatusBadge value={l.status} /></td>
                                    <td>
                                        {l.status === "submitted" && (
                                            <div className="flex gap-1">
                                                <button onClick={() => decide("leaves", l.id, "approved")} className="h-7 px-2 text-xs rounded-md bg-emerald-600 text-white" data-testid={`approve-leave-${l.id}`}><Check className="size-3 inline mr-1" />Approve</button>
                                                <button onClick={() => decide("leaves", l.id, "rejected")} className="h-7 px-2 text-xs rounded-md bg-destructive text-white" data-testid={`reject-leave-${l.id}`}><X className="size-3 inline mr-1" />Reject</button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {!leaves.length && <tr><td colSpan={7}><Empty title="No leave requests" /></td></tr>}
                        </tbody>
                    </TableWrap>
                </TabsContent>
                <TabsContent value="overtime">
                    <TableWrap>
                        <thead><tr><th>Employee</th><th>Date</th><th>Hours</th><th>Reason</th><th>Status</th><th>Action</th></tr></thead>
                        <tbody>
                            {ot.map((o) => (
                                <tr key={o.id} data-testid={`team-ot-${o.id}`}>
                                    <td>{empMap[o.employee_id] || o.employee_id?.slice(0, 8)}</td>
                                    <td className="font-mono">{o.work_date}</td>
                                    <td>{o.hours}</td>
                                    <td>{o.reason || "—"}</td>
                                    <td><StatusBadge value={o.status} /></td>
                                    <td>
                                        {o.status === "submitted" && (
                                            <div className="flex gap-1">
                                                <button onClick={() => decide("overtime", o.id, "approved")} className="h-7 px-2 text-xs rounded-md bg-emerald-600 text-white" data-testid={`approve-ot-${o.id}`}><Check className="size-3 inline mr-1" />Approve</button>
                                                <button onClick={() => decide("overtime", o.id, "rejected")} className="h-7 px-2 text-xs rounded-md bg-destructive text-white" data-testid={`reject-ot-${o.id}`}><X className="size-3 inline mr-1" />Reject</button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {!ot.length && <tr><td colSpan={6}><Empty title="No OT requests" /></td></tr>}
                        </tbody>
                    </TableWrap>
                </TabsContent>
                <TabsContent value="attendance">
                    <TableWrap>
                        <thead><tr><th>Date</th><th>Employee</th><th>Time In</th><th>Time Out</th><th className="num">Hours</th><th>Geofence</th></tr></thead>
                        <tbody>
                            {att.slice(0, 100).map((a) => (
                                <tr key={a.id} data-testid={`team-att-${a.id}`}>
                                    <td className="font-mono">{a.work_date}</td>
                                    <td>{empMap[a.employee_id] || a.employee_id?.slice(0, 8)}</td>
                                    <td className="font-mono text-xs">{a.time_in?.slice(0, 16) || "—"}</td>
                                    <td className="font-mono text-xs">{a.time_out?.slice(0, 16) || "—"}</td>
                                    <td className="num">{(a.hours_worked || 0).toFixed(2)}</td>
                                    <td>{a.geofence_result ? <StatusBadge value={a.geofence_result} /> : "—"}</td>
                                </tr>
                            ))}
                            {!att.length && <tr><td colSpan={6}><Empty title="No attendance records" /></td></tr>}
                        </tbody>
                    </TableWrap>
                </TabsContent>
            </Tabs>
        </div>
    );
}
